import React, {FC, useMemo} from "react";
import {DownloadOutlined, MailOutlined} from "@ant-design/icons";
import {Button, Form, Input, Modal, Space, Table, Typography} from "antd";
import type {TableColumnsType} from "antd";
import {useTranslation} from "react-i18next";
import {FieldTile, StatusTag} from "../../../Components/ui";
import {normalizeEnumKey} from "../../../i18n/labelHelpers";
import {formatLocalDateTime} from "../adminDateFormat";
import {buildParticipantOperationsFileDownloadUrl, EMPTY_OPERATIONS_VALUE} from "../participantOperationsHelpers";
import {dateSorter, sortText, textSorter} from "../tableSorters";
import {displayValue, emailDeliveryTone, isRegistrationRejectable} from "./participantDetailHelpers";
import type {ParticipantRegistration} from "./participantDetailTypes";
import {DetailPanel, EnumTag, FIELD_GRID_STYLE, GdprConsentSummary, useEmptyText, useEnumLabel} from "./participantDetailUi";
import {ParticipantFormField} from "./ParticipantFormField";
import {optionalLinkedInUrlRules, requiredEmailRules, requiredPhoneRules, requiredTextRules} from "../../publicRegistration/formRules";
import type {ParticipantDetailModel} from "./useParticipantDetail";

// Participant contact data and the registrations recorded for this event.
export const RegistrationSection: FC<{model: ParticipantDetailModel}> = ({model}) => {
    const {t} = useTranslation();
    // Same participant rules as the registration form (and the server).
    const ruleLabels = {
        required: t("validation-required", {defaultValue: "Required field"}),
        invalidEmail: t("validation-invalid-email", {defaultValue: "Enter a valid email address."}),
        invalidPhone: t("validation-invalid-phone", {defaultValue: "Enter a valid phone number."}),
        invalidLinkedInUrl: t("validation-invalid-linkedin", {defaultValue: "Enter a LinkedIn profile URL."})
    };
    const enumLabel = useEnumLabel();
    const emptyText = useEmptyText();
    const {registrations, pending, actions, forms, rejectingRegistration} = model;
    const verifiedRegistrationCount = registrations.filter(registration => registration.isEmailVerified).length;

    const registrationColumns = useMemo<TableColumnsType<ParticipantRegistration>>(() => [
        {
            key: "registration",
            title: t("registration", {defaultValue: "Registration"}),
            sorter: textSorter(registration => sortText(
                registration.status,
                registration.isEmailVerified ? "email verified" : "email unverified",
                registration.gdprConsentAccepted ? "gdpr accepted" : "gdpr missing"
            )),
            render: (_, registration) => (
                <Space direction="vertical" size={2}>
                    <EnumTag value={registration.status}/>
                    {registration.isEmailVerified ? (
                        <StatusTag tone="success">{t("email-verified", {defaultValue: "Verified"})}</StatusTag>
                    ) : (
                        <StatusTag tone="neutral">{t("email-unverified", {defaultValue: "Unverified"})}</StatusTag>
                    )}
                    {!registration.isEmailVerified && registration.verificationEmailStatus ? (
                        <StatusTag tone={emailDeliveryTone(registration.verificationEmailStatus)}>
                            {t("verification-email", {defaultValue: "Verification email"})}: {enumLabel(registration.verificationEmailStatus)}
                        </StatusTag>
                    ) : null}
                    {registration.verificationEmailFailureReason?.trim() ? (
                        <Typography.Text type="danger">{registration.verificationEmailFailureReason}</Typography.Text>
                    ) : null}
                    {!registration.isEmailVerified && normalizeEnumKey(registration.verificationEmailStatus) === "FAILED" ? (
                        <Button
                            size="small"
                            icon={<MailOutlined/>}
                            loading={pending.resendingVerificationRegistrationId === registration.id}
                            onClick={() => actions.resendVerificationEmail(registration)}
                        >
                            {t("resend-verification-email", {defaultValue: "Resend verification"})}
                        </Button>
                    ) : null}
                    <GdprConsentSummary registration={registration}/>
                    {registration.rejectionReason ? (
                        <Typography.Text type="danger">{registration.rejectionReason}</Typography.Text>
                    ) : null}
                </Space>
            )
        },
        {
            key: "payer",
            title: t("payer", {defaultValue: "Payer"}),
            sorter: textSorter(registration => sortText(
                registration.payerName,
                registration.payerType,
                registration.payerEmail
            )),
            render: (_, registration) => (
                <Space direction="vertical" size={2}>
                    <Typography.Text>{displayValue(registration.payerName)}</Typography.Text>
                    <Typography.Text type="secondary">{enumLabel(registration.payerType)}</Typography.Text>
                    <Typography.Text type="secondary">{displayValue(registration.payerEmail)}</Typography.Text>
                </Space>
            )
        },
        {
            key: "participantTypeProof",
            title: t("participant-type-proof", {defaultValue: "Participant proof"}),
            sorter: textSorter(registration => sortText(
                registration.participantTypeProofFileName,
                registration.participantTypeProofFileId ? "ready" : "missing"
            )),
            render: (_, registration) => registration.participantTypeProofFileId ? (
                <Button
                    size="small"
                    icon={<DownloadOutlined/>}
                    href={buildParticipantOperationsFileDownloadUrl(registration.participantTypeProofFileId)}
                    target="_blank"
                    rel="noreferrer"
                >
                    {registration.participantTypeProofFileName?.trim() || t("download", {defaultValue: "Download"})}
                </Button>
            ) : (
                <Typography.Text type="secondary">{EMPTY_OPERATIONS_VALUE}</Typography.Text>
            )
        },
        {
            key: "created",
            title: t("created", {defaultValue: "Created"}),
            sorter: dateSorter(registration => registration.createdAt),
            render: (_, registration) => formatLocalDateTime(registration.createdAt, EMPTY_OPERATIONS_VALUE)
        },
        {
            key: "actions",
            title: t("actions", {defaultValue: "Actions"}),
            render: (_, registration) => (
                <Button
                    danger
                    size="small"
                    disabled={!isRegistrationRejectable(registration.status)}
                    loading={pending.savingRegistration && rejectingRegistration?.id === registration.id}
                    onClick={() => actions.openRegistrationReject(registration)}
                >
                    {t("reject-registration", {defaultValue: "Reject"})}
                </Button>
            )
        }
    ], [actions, enumLabel, pending.resendingVerificationRegistrationId, pending.savingRegistration, rejectingRegistration?.id, t]);

    return (
        <>
            <DetailPanel
                title={t("participant-basics-title", {defaultValue: "Participant details"})}
                extra={(
                    <Button type="primary" loading={pending.savingParticipant} onClick={() => forms.participant.submit()}>
                        {t("save-participant-details", {defaultValue: "Save participant details"})}
                    </Button>
                )}
            >
                <Form form={forms.participant} onFinish={actions.saveParticipant}>
                    <div style={FIELD_GRID_STYLE}>
                        <ParticipantFormField label={t("first-name", {defaultValue: "First name"})} name="firstName" rules={requiredTextRules(ruleLabels)}>
                            <Input aria-label={t("first-name", {defaultValue: "First name"})}/>
                        </ParticipantFormField>
                        <ParticipantFormField label={t("last-name", {defaultValue: "Last name"})} name="lastName" rules={requiredTextRules(ruleLabels)}>
                            <Input aria-label={t("last-name", {defaultValue: "Last name"})}/>
                        </ParticipantFormField>
                        <ParticipantFormField
                            label={t("institution-company", {defaultValue: "Institution/Company"})}
                            name="company"
                            rules={requiredTextRules(ruleLabels)}
                        >
                            <Input aria-label={t("institution-company", {defaultValue: "Institution/Company"})}/>
                        </ParticipantFormField>
                        <ParticipantFormField label={t("email", {defaultValue: "Email"})} name="email" rules={requiredEmailRules(ruleLabels)}>
                            <Input aria-label={t("email", {defaultValue: "Email"})}/>
                        </ParticipantFormField>
                        <ParticipantFormField label={t("phone", {defaultValue: "Phone"})} name="phoneNumber" rules={requiredPhoneRules(ruleLabels)}>
                            <Input aria-label={t("phone", {defaultValue: "Phone"})}/>
                        </ParticipantFormField>
                        <ParticipantFormField label={t("linkedin-url", {defaultValue: "LinkedIn URL"})} name="linkedInUrl" rules={optionalLinkedInUrlRules(ruleLabels)}>
                            <Input aria-label={t("linkedin-url", {defaultValue: "LinkedIn URL"})}/>
                        </ParticipantFormField>
                    </div>
                </Form>
            </DetailPanel>

            <DetailPanel title={t("registrations", {defaultValue: "Registrations"})}>
                <div style={FIELD_GRID_STYLE}>
                    <FieldTile
                        label={t("email-verification", {defaultValue: "Email verification"})}
                        detail={registrations.length > 0
                            ? t("verified-registration-count", {
                                count: verifiedRegistrationCount,
                                total: registrations.length,
                                defaultValue: "{{count}} of {{total}} verified"
                            })
                            : undefined}
                    >
                        {verifiedRegistrationCount > 0
                            ? <StatusTag tone="success">{t("verified", {defaultValue: "Verified"})}</StatusTag>
                            : <StatusTag>{t("not-verified", {defaultValue: "Unverified"})}</StatusTag>}
                    </FieldTile>
                </div>
                <Table
                    size="small"
                    rowKey="id"
                    columns={registrationColumns}
                    dataSource={registrations}
                    pagination={false}
                    scroll={{x: 900}}
                    locale={{emptyText}}
                />
            </DetailPanel>

            <Modal
                title={t("reject-registration", {defaultValue: "Reject"})}
                open={!!rejectingRegistration}
                okText={t("reject-registration", {defaultValue: "Reject"})}
                okButtonProps={{danger: true, loading: pending.savingRegistration}}
                cancelText={t("cancel", {defaultValue: "Cancel"})}
                onOk={() => forms.reject.submit()}
                onCancel={actions.closeRegistrationReject}
                destroyOnHidden
            >
                <Form form={forms.reject} layout="vertical" onFinish={actions.rejectRegistration}>
                    <Form.Item
                        name="reason"
                        label={t("participant-rejection-reason-prompt", {defaultValue: "Enter the reason for rejecting this registration."})}
                        rules={[{
                            required: true,
                            whitespace: true,
                            message: t("participant-rejection-reason-required", {defaultValue: "A rejection reason is required."})
                        }]}
                    >
                        <Input.TextArea
                            rows={3}
                            aria-label={t("participant-rejection-reason-prompt", {defaultValue: "Enter the reason for rejecting this registration."})}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};
